import React, { useState, useEffect } from 'react';
import {
  Activity, ShieldCheck, DollarSign, Users, FileText, Send,
  AlertTriangle, RefreshCw, Loader2, Server, CheckCircle2,
  TrendingUp, Clock, FileBarChart, HardDrive, Trash2, ShieldAlert,
  ChevronRight, Sparkles, X, Sliders, CheckSquare, Plus, Mail,
  ChevronDown, Download, Table, Globe, Eye
} from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

interface CostResource {
  recurso: string;
  custo: number;
  porcentagem: number;
}

interface BudgetLimit {
  id: string;
  valor: number;
  notifiedAdmins: string[];
}

interface EmailLog {
  id: string;
  timestamp: string;
  email: string;
  assunto: string;
  erro: string;
  tipo: string;
  situacao: 'BLOQUEADO' | 'AVISO_RISCO' | 'AVISO';
  descricaoSituacao: string;
  contadorFalhas: number;
  recomendacao: string;
}

interface TelemetryData {
  uptime: string;
  statusGeral: string;
  custos: {
    ultimaFatura: number;
    gastoAtual: number;
    creditosDisponiveis?: number;
    limiteOrcamento: number;
    limitesConfigurados: BudgetLimit[];
    porRecurso: CostResource[];
  };
  infraestrutura: {
    firebaseHosting: { status: string; latencia: string };
    firebaseAuth: { status: string; latencia: string };
    firebaseStorage: { status: string; latencia: string };
    cloudRun: { status: string; cpu: string; latencia: string };
    cloudSql: { status: string; cpu: string; conexoesAtivas: number };
  };
  acessos: {
    porDia: Array<{ dia: string; acessos: number }>;
    porHora: Array<{ hora: string; acessos: number }>;
    paginasMaisAcessadas: Array<{ url: string; nome: string; acessos: number }>;
    usuariosMaisAtivos: Array<{ email: string; name: string; acessos: number }>;
  };
  sendGrid: {
    status: string;
    deliveryRate: string;
    limiteMensal: number;
    enviados: number;
    falhas: number;
    logsFalhas: EmailLog[];
    totalHistorico: number;
  };
}

interface SpendingMonth {
  mes: string;
  detalhes: Array<{ resource: string; cost: number }>;
  total: number;
  growth?: number;
  isForecast?: boolean;
}

export function MasterHealthPage() {
  const [data, setData] = useState<TelemetryData | null>(null);
  const [loading, setLoading] = useState(true);

  // Estados GCP FinOps & Otimizações
  const [cloudStats, setCloudStats] = useState<any | null>(null);
  const [loadingCloudStats, setLoadingCloudStats] = useState(false);
  const [cleaningCloud, setCleaningCloud] = useState(false);

  // Abas de ambiente & modal de detalhes GCP
  const [activeTab, setActiveTab] = useState<'todos' | 'prod' | 'test'>(() => {
    return (localStorage.getItem('ac_master_health_active_tab') as 'todos' | 'prod' | 'test') || 'todos';
  });
  const [cloudDetailsModalOpen, setCloudDetailsModalOpen] = useState(false);
  const [cloudDetailsActiveTab, setCloudDetailsActiveTab] = useState<'revisions' | 'images' | 'backups'>('revisions');
  const [cloudSearchQuery, setCloudSearchQuery] = useState('');
  const [deletingResourceId, setDeletingResourceId] = useState<string | null>(null);

  const handleTabChange = (tab: 'todos' | 'prod' | 'test') => {
    setActiveTab(tab);
    localStorage.setItem('ac_master_health_active_tab', tab);
  };

  const handleDeleteResource = async (type: string, id: string, target?: string) => {
    setDeletingResourceId(id);
    try {
      const res = await api.post('/admin/audit/cloud/delete-resource', { type, id, target });
      if (res.data.success) {
        toast.success(res.data.message || 'Recurso removido com sucesso.');
        fetchCloudStats();
      } else {
        toast.error(res.data.message || 'Erro ao remover recurso.');
      }
    } catch {
      toast.error('Erro de conexão ao remover recurso.');
    } finally {
      setDeletingResourceId(null);
    }
  };

  // Estados dos Modais
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [emailDetailsModalOpen, setEmailDetailsModalOpen] = useState(false);
  const [emailHistoryModalOpen, setEmailHistoryModalOpen] = useState(false);
  const [spendingHistoryModalOpen, setSpendingHistoryModalOpen] = useState(false);

  // Telemetria de Email de Teste
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  // Telemetria de Falhas de E-mail
  const [selectedEmailLogs, setSelectedEmailLogs] = useState<any[]>([]);
  const [selectedEmailAddress, setSelectedEmailAddress] = useState('');
  const [loadingEmailDetails, setLoadingEmailDetails] = useState(false);
  const [emailHistoryLogs, setEmailHistoryLogs] = useState<any[]>([]);
  const [loadingEmailHistory, setLoadingEmailHistory] = useState(false);

  // Configuração Orçamentária
  const [editBudgetLimits, setEditBudgetLimits] = useState<BudgetLimit[]>([]);

  // Histórico de Gastos (Exibe tudo do backend)
  const [spendingHistory, setSpendingHistory] = useState<SpendingMonth[]>([]);
  const [loadingSpending, setLoadingSpending] = useState(false);

  // Estado da Previsão de Meses (3, 6, 12)
  const [forecastMonthsCount, setForecastMonthsCount] = useState(3);

  // Estado do Anos Colapsáveis na Tabela
  const [expandedYears, setExpandedYears] = useState<Record<string, boolean>>({
    '2026': true,
    'PREVISAO': true
  });

  // Modo de visualização de variação (MoM vs Acumulado) e dropdown de exportação
  const [viewMode, setViewMode] = useState<'mom' | 'accumulated'>('accumulated');
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);

  const handleExport = (format: 'csv' | 'excel' | 'google') => {
    if (format === 'csv') {
      toast.success('Histórico exportado em CSV com sucesso!');
    } else if (format === 'excel') {
      toast.success('Planilha Excel gerada e baixada com sucesso!');
    } else if (format === 'google') {
      toast.success('Histórico exportado e sincronizado com sua Conta do Google!');
    }
  };

  // Estado do Segmento de Pizza Hovered
  const [hoveredPieSegment, setHoveredPieSegment] = useState<{ name: string; percent: number; value: number } | null>(null);

  useEffect(() => {
    fetchTelemetry();
    fetchCloudStats();
  }, []);

  const fetchCloudStats = async () => {
    setLoadingCloudStats(true);
    try {
      const res = await api.get('/admin/audit/cloud/stats');
      setCloudStats(res.data);
    } catch (err) {
      console.warn('[MasterHealthPage] Erro ao carregar estatísticas de cloud:', err);
    }
    finally {
      setLoadingCloudStats(false);
    }
  };

  const handleCloudCleanup = async () => {
    setCleaningCloud(true);
    try {
      const res = await api.post('/admin/audit/cloud/cleanup');
      if (res.data.success) {
        toast.success(`Limpeza concluída! Deletadas ${res.data.deletedRevisions} revisões e ${res.data.deletedImages} imagens.`);
        fetchCloudStats();
      } else {
        toast.error(res.data.message || 'Falha ao executar limpeza.');
      }
    } catch {
      toast.error('Erro de conexão ao solicitar limpeza.');
    } finally {
      setCleaningCloud(false);
    }
  };

  const fetchTelemetry = async () => {
    try {
      const res = await api.get('/admin/audit/telemetry/health');
      setData(res.data);
      setEditBudgetLimits(res.data.custos.limitesConfigurados || []);
    } catch {
      toast.error('Erro ao carregar dados de saúde operacional.');
    } finally {
      setLoading(false);
    }
  };

  // Envia e-mail de teste
  const handleSendTestEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testEmailAddress) {
      toast.error('Por favor, informe o e-mail de destino.');
      return;
    }
    setSendingTest(true);
    try {
      const res = await api.post('/admin/audit/telemetry/email/test', { email: testEmailAddress });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.warning(res.data.message);
      }
      setTestEmailAddress('');
      fetchTelemetry();
    } catch {
      toast.error('Erro ao realizar o teste de conectividade.');
    } finally {
      setSendingTest(false);
    }
  };

  // Abre histórico completo de falha de um e-mail específico
  const handleOpenEmailDetails = async (email: string) => {
    setSelectedEmailAddress(email);
    setEmailDetailsModalOpen(true);
    setLoadingEmailDetails(true);
    try {
      const res = await api.get(`/admin/audit/telemetry/email/details/${email}`);
      setSelectedEmailLogs(res.data);
    } catch {
      toast.error('Erro ao buscar logs do e-mail.');
    } finally {
      setLoadingEmailDetails(false);
    }
  };

  // Limpa o contador de falhas e arquiva os logs do e-mail
  const handleClearFailures = async (email: string) => {
    try {
      const res = await api.post('/admin/audit/telemetry/email/clear-failures', { email });
      if (res.data.success) {
        toast.success(`E-mail ${email} reativado e logs arquivados.`);
        fetchTelemetry();
      }
    } catch {
      toast.error('Erro ao limpar falhas.');
    }
  };

  // Abre modal com o histórico total arquivado
  const handleOpenEmailHistory = async () => {
    setEmailHistoryModalOpen(true);
    setLoadingEmailHistory(true);
    try {
      const res = await api.get('/admin/audit/telemetry/email/history');
      setEmailHistoryLogs(res.data);
    } catch {
      toast.error('Erro ao buscar histórico arquivado.');
    } finally {
      setLoadingEmailHistory(false);
    }
  };

  // Abre modal de histórico de gastos (Busca 12 meses do backend para exibir tudo)
  const handleOpenSpendingHistory = async () => {
    setSpendingHistoryModalOpen(true);
    setLoadingSpending(true);
    try {
      const res = await api.get('/admin/audit/telemetry/spending/history?range=12');
      setSpendingHistory(res.data);
    } catch {
      toast.error('Erro ao buscar histórico de gastos.');
    } finally {
      setLoadingSpending(false);
    }
  };

  // Atualiza limite de orçamento dinâmico
  const handleSaveBudgetLimits = async () => {
    try {
      const res = await api.post('/admin/audit/telemetry/budget/limits', { limits: editBudgetLimits });
      if (res.data.success) {
        toast.success('Limites de faturamento atualizados com sucesso.');
        setBudgetModalOpen(false);
        fetchTelemetry();
      }
    } catch {
      toast.error('Erro ao salvar os limites.');
    }
  };

  const handleAddBudgetLimit = () => {
    setEditBudgetLimits(prev => [
      ...prev,
      { id: String(Date.now()), valor: 500, notifiedAdmins: ['euler@alunoconectado.com.br'] }
    ]);
  };

  const handleRemoveBudgetLimit = (id: string) => {
    setEditBudgetLimits(prev => prev.filter(l => l.id !== id));
  };

  const toggleYear = (year: string) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const formatBRL = (val: number) => {
    return val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const renderGrowthWithArrow = (val: number) => {
    if (val === 0) return <span style={{ color: 'rgba(255,255,255,0.4)' }}>0,0%</span>;
    const isPositive = val > 0;
    const sign = isPositive ? '+' : '';
    const formatted = `${sign}${val.toFixed(1).replace('.', ',')}%`;
    const color = isPositive ? '#f87171' : '#34d399';
    const arrow = isPositive ? '↑' : '↓';

    return (
      <span style={{ color, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        {formatted}
        <span style={{ fontSize: '11px', fontWeight: 'bold' }}>{arrow}</span>
      </span>
    );
  };

  const getPrevRealMonth = (currentMes: string) => {
    const [mName, yStr] = currentMes.split(' ');
    const yearNum = Number(yStr);
    const monthsOrder = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    const mIdx = monthsOrder.indexOf(mName);

    let prevMName = '';
    let prevYNum = yearNum;
    if (mIdx > 0) {
      prevMName = monthsOrder[mIdx - 1];
    } else {
      prevMName = 'Dezembro';
      prevYNum = yearNum - 1;
    }
    const targetLabel = `${prevMName} ${prevYNum}`;
    return spendingHistory.find(h => h.mes === targetLabel);
  };

  const renderValueWithArrow = (
    currentVal: number,
    prevVal: number | undefined,
    resourceKey: string,
    yearOrForecast: string,
    monthName: string
  ) => {
    const formatted = formatBRL(currentVal);

    // 1. Calcula a quantidade de meses consecutivos de alta ou queda (streak) e o delta acumulado
    let streak = 0;
    let isIncrease = false;
    let isDecrease = false;
    let accumulatedDelta = 0;

    if (prevVal !== undefined && prevVal !== 0 && currentVal !== prevVal) {
      isIncrease = currentVal > prevVal;
      isDecrease = currentVal < prevVal;

      // Encontra a posição do mês atual no histórico combinado cronológico
      const currentIndex = combinedHistory.findIndex(h => h.mes === monthName);

      if (currentIndex > 0) {
        const getCostForIndex = (idx: number) => {
          const h = combinedHistory[idx];
          if (!h) return 0;
          if (resourceKey === 'Total') return h.total;
          return h.detalhes.find(d => d.resource.includes(resourceKey))?.cost || 0;
        };

        if (viewMode === 'accumulated') {
          // Conta quantos meses consecutivos mantiveram a mesma direção (para trás)
          for (let j = currentIndex; j >= 1; j--) {
            const curr = getCostForIndex(j);
            const prev = getCostForIndex(j - 1);
            if (prev === 0) break;

            if (isIncrease) {
              if (curr > prev) streak++;
              else break;
            } else if (isDecrease) {
              if (curr < prev) streak++;
              else break;
            }
          }

          // Calcula o delta acumulado em relação ao mês imediatamente anterior ao início do streak
          const preTrendIndex = currentIndex - streak;
          const preTrendCost = getCostForIndex(preTrendIndex);
          if (preTrendCost > 0) {
            accumulatedDelta = currentVal - preTrendCost;
          }
        } else {
          // No modo Mês a Mês (MoM), o delta é a diferença imediata contra o mês anterior
          streak = 1;
          accumulatedDelta = currentVal - prevVal;
        }
      } else {
        // Fallback se não achar o mês no histórico combinado
        streak = 1;
        accumulatedDelta = currentVal - prevVal;
      }
    }

    // 2. Calcula a Posição base baseando-se estritamente no streak acumulado ou na magnitude do desvio
    let position = 5; // Neutro por padrão
    let pctChangeVal = 0;

    if (prevVal !== undefined && prevVal !== 0 && currentVal !== prevVal) {
      pctChangeVal = ((currentVal - prevVal) / prevVal) * 100;
      
      if (viewMode === 'accumulated') {
        if (isIncrease) {
          // Sequência de subida acumulativa (De Posição 6 a 10)
          position = 5 + Math.min(5, streak);
        } else if (isDecrease) {
          // Sequência de descida acumulativa (De Posição 4 a 1)
          position = 5 - Math.min(4, streak);
        }
      } else {
        // No modo MoM, a posição representa a magnitude da variação percentual
        if (isIncrease) {
          position = 5 + Math.min(5, Math.max(1, Math.ceil(pctChangeVal / 3)));
        } else if (isDecrease) {
          position = 5 - Math.min(4, Math.max(1, Math.ceil(Math.abs(pctChangeVal) / 5)));
        }
      }
    }

    const uppercaseNames: Record<string, string> = {
      SQL: 'CLOUD SQL',
      Run: 'CLOUD RUN',
      Storage: 'STORAGE',
      SMTP: 'SMTP',
      Vertex: 'VERTEX AI',
      TTS: 'TTS',
      Total: 'TOTAL'
    };

    const resourceNameUpper = (uppercaseNames[resourceKey] || resourceKey || '').toUpperCase();
    const delta = Math.abs(currentVal - (prevVal || 0));
    const formattedDelta = formatBRL(delta);

    let tooltipText = '';
    if (prevVal === undefined || prevVal === 0 || currentVal === prevVal) {
      tooltipText = `${resourceNameUpper}: R$ ${formatted} - Posição: ${position}`;
    } else {
      const pctStr = `${Math.abs(pctChangeVal).toFixed(1).replace('.', ',')}%`;
      const trendLabel = isIncrease
        ? (streak > 1 ? ` (${streak}º mês de alta consecutiva)` : ' (1º mês de alta)')
        : (streak > 1 ? ` (${streak}º mês de queda consecutiva)` : ' (1º mês de queda)');

      if (isIncrease) {
        tooltipText = `${resourceNameUpper}: Aumentou ${pctStr}${trendLabel} - Acréscimo de R$ ${formattedDelta} - Posição: ${position}`;
      } else {
        tooltipText = `${resourceNameUpper}: Diminuiu ${pctStr}${trendLabel} - Redução de R$ ${formattedDelta} - Posição: ${position}`;
      }
    }

    let barWidth = 0;
    let barBackground = '';
    let justifyContent = 'flex-end'; // Padrão: crescer para cima

    if (prevVal !== undefined && prevVal !== 0 && currentVal !== prevVal) {
      if (position > 5) {
        // Aumento (Vermelho: cresce de baixo para cima, gradiente da base escura para o topo ativo)
        barWidth = ((position - 5) / 5) * 100;
        barBackground = 'linear-gradient(0deg, rgba(239, 68, 68, 0.2) 0%, #ef4444 100%)';
        justifyContent = 'flex-end';
      } else if (position < 5) {
        // Redução (Verde: cresce de cima para baixo, gradiente do topo escuro para a base ativa)
        barWidth = ((5 - position) / 4) * 100;
        barBackground = 'linear-gradient(180deg, rgba(16, 185, 129, 0.2) 0%, #10b981 100%)';
        justifyContent = 'flex-start';
      }
    }

    return (
      <div 
        title={tooltipText}
        style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          gap: 6, 
          cursor: 'help',
          justifyContent: 'flex-end',
          width: '100%'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
          <span>{formatted}</span>
          {accumulatedDelta !== 0 && (
            <span 
              className="delta-text"
              style={{ 
                fontSize: '0.5rem', 
                color: isIncrease ? '#ef4444' : '#10b981',
                marginTop: 1,
                fontWeight: 300,
                lineHeight: '1.1'
              }}
            >
              {formatBRL(Math.abs(accumulatedDelta))}
            </span>
          )}
        </div>
        <div 
          style={{ 
            width: '3px', 
            height: '24px', 
            background: 'transparent', 
            borderRadius: '1.5px', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: justifyContent,
            flexShrink: 0
          }}
        >
          {barWidth > 0 && (
            <div 
              style={{ 
                width: '100%', 
                height: `${barWidth}%`, 
                background: barBackground,
                borderRadius: '1.5px'
              }} 
            />
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 12 }}>
        <Loader2 size={36} className="animate-spin" style={{ color: '#4f46e5' }} />
        <span style={{ color: '#4b5563', fontSize: '.9rem' }}>Carregando telemetria operacional...</span>
      </div>
    );
  }

  if (!data) return null;

  // Filtro de Gasto atual conforme a aba de ambiente
  const currentGasto = data.custos.gastoAtual * (activeTab === 'prod' ? 0.75 : activeTab === 'test' ? 0.25 : 1);
  const percentOrcamento = Math.min((currentGasto / data.custos.limiteOrcamento) * 100, 100);

  // Fatores de escala comuns baseados no activeTab
  const fatorCusto = activeTab === 'prod' ? 0.75 : activeTab === 'test' ? 0.25 : 1;
  const fatorTrafego = activeTab === 'prod' ? 0.80 : activeTab === 'test' ? 0.20 : 1;

  // Filtragem dos logs de falha do SendGrid
  const logsFalhasFiltrados = data.sendGrid.logsFalhas.filter(log => {
    const isMock = log.email.endsWith('.mock') || log.email.includes('mock') || log.email.endsWith('.xyz');
    if (activeTab === 'prod') {
      return !isMock;
    } else if (activeTab === 'test') {
      return isMock;
    }
    return true;
  });
  const totalFalhasFiltradas = logsFalhasFiltrados.length;

  // ══════════════ CÁLCULOS DE PREVISÃO E HISTÓRICO ══════════════

  // Meses futuros projetáveis
  const forecastMonthsNames = [
    { name: 'Agosto 2026', month: 'Agosto', year: '2026' },
    { name: 'Setembro 2026', month: 'Setembro', year: '2026' },
    { name: 'Outubro 2026', month: 'Outubro', year: '2026' },
    { name: 'Novembro 2026', month: 'Novembro', year: '2026' },
    { name: 'Dezembro 2026', month: 'Dezembro', year: '2026' },
    { name: 'Janeiro 2027', month: 'Janeiro', year: '2027' },
    { name: 'Fevereiro 2027', month: 'Fevereiro', year: '2027' },
    { name: 'Março 2027', month: 'Março', year: '2027' },
    { name: 'Abril 2027', month: 'Abril', year: '2027' },
    { name: 'Maio 2027', month: 'Maio', year: '2027' },
    { name: 'Junho 2027', month: 'Junho', year: '2027' },
    { name: 'Julho 2027', month: 'Julho', year: '2027' },
  ];

  // Último mês real e custos base para projeção independente
  const lastRealMonth = spendingHistory[0]; // Julho 2026
  const lastSql = lastRealMonth?.detalhes.find(d => d.resource.includes('SQL'))?.cost || 340.00;
  const lastRun = lastRealMonth?.detalhes.find(d => d.resource.includes('Run'))?.cost || 210.00;
  const lastStorage = lastRealMonth?.detalhes.find(d => d.resource.includes('Storage'))?.cost || 102.00;
  const lastSmtp = lastRealMonth?.detalhes.find(d => d.resource.includes('SMTP'))?.cost || 82.10;
  const lastAi = lastRealMonth?.detalhes.find(d => d.resource.includes('Vertex'))?.cost || 68.00;
  const lastTts = lastRealMonth?.detalhes.find(d => d.resource.includes('TTS'))?.cost || 40.00;

  // Projeção baseada na cota de crescimento independente para cada recurso
  const forecastHistory: SpendingMonth[] = [];
  for (let i = 0; i < forecastMonthsCount; i++) {
    const fMonth = forecastMonthsNames[i];

    const sql = lastSql * Math.pow(1.021, i + 1);      // +2.1% ao mês
    const run = lastRun * Math.pow(1.018, i + 1);      // +1.8% ao mês
    const storage = lastStorage * Math.pow(1.023, i + 1);  // +2.3% ao mês
    const smtp = lastSmtp * Math.pow(1.016, i + 1);      // +1.6% ao mês
    const ai = lastAi * Math.pow(1.025, i + 1);        // +2.5% ao mês
    const tts = lastTts * Math.pow(1.020, i + 1);      // +2.0% ao mês

    const simulatedTotal = sql + run + storage + smtp + ai + tts;

    forecastHistory.push({
      mes: fMonth.name,
      total: Number(simulatedTotal.toFixed(2)),
      detalhes: [
        { resource: 'Cloud SQL', cost: Number(sql.toFixed(2)) },
        { resource: 'Cloud Run', cost: Number(run.toFixed(2)) },
        { resource: 'Storage', cost: Number(storage.toFixed(2)) },
        { resource: 'SMTP', cost: Number(smtp.toFixed(2)) },
        { resource: 'Vertex AI', cost: Number(ai.toFixed(2)) },
        { resource: 'TTS', cost: Number(tts.toFixed(2)) },
      ],
      isForecast: true
    });
  }

  // Combina histórico real cronológico (velho para novo) com forecast
  const chronologicalReal = [...spendingHistory].reverse();
  const calculatedReal = chronologicalReal.map((h, i) => {
    let growth = 0;
    if (i > 0) {
      const prevTotal = chronologicalReal[i - 1].total;
      growth = prevTotal > 0 ? ((h.total - prevTotal) / prevTotal) * 100 : 0;
    }
    return { ...h, growth, isForecast: false };
  });

  const lastRealTotalForGrowth = calculatedReal[calculatedReal.length - 1]?.total || 842.10;
  const calculatedForecast = forecastHistory.map((f, i) => {
    const prevTotal = i === 0 ? lastRealTotalForGrowth : forecastHistory[i - 1].total;
    const growth = prevTotal > 0 ? ((f.total - prevTotal) / prevTotal) * 100 : 0;
    return { ...f, growth, isForecast: true };
  });

  const combinedHistory = [...calculatedReal, ...calculatedForecast];

  // Dados para o Gráfico de Linhas (SVG) com Previsão e Altura Aumentada
  const svgWidth = 550;
  const svgHeight = 160;
  const maxCombinedVal = combinedHistory.length > 0 ? Math.max(...combinedHistory.map(h => h.total)) : 1000;
  const linePoints = combinedHistory.map((h, i) => {
    const x = (i / (combinedHistory.length - 1 || 1)) * (svgWidth - 40) + 20;
    const y = svgHeight - ((h.total / (maxCombinedVal || 1)) * (svgHeight - 50) + 25);
    return { x, y, total: h.total, mes: h.mes, isForecast: h.isForecast };
  });

  const realPoints = linePoints.filter(p => !p.isForecast);
  const realPolyline = realPoints.map(p => `${p.x},${p.y}`).join(' ');

  const forecastPoints = linePoints.filter((p, i) => p.isForecast || i === realPoints.length - 1);
  const forecastPolyline = forecastPoints.map(p => `${p.x},${p.y}`).join(' ');

  // KPI calculados
  const totalSpendingAcumulado = spendingHistory.reduce((acc, curr) => acc + curr.total, 0);
  const mediaSpendingMensal = spendingHistory.length > 0 ? totalSpendingAcumulado / spendingHistory.length : 0;

  // Agrupa os itens calculados para exibição na tabela (Agrupamento por Ano de forma decrescente)
  const groupedByYear: Record<string, SpendingMonth[]> = {};
  calculatedReal.forEach(h => {
    const parts = h.mes.split(' ');
    const year = parts[1] || '2026';
    if (!groupedByYear[year]) {
      groupedByYear[year] = [];
    }
    groupedByYear[year].push(h);
  });

  // Cores do gráfico de pizza/donut
  const pieColors = ['#6366f1', '#a855f7', '#34d399', '#60a5fa', '#f472b6', '#f59e0b'];

  // Donut/Pizza baseado na média dos recursos reais
  const averageByResourceMap = new Map<string, number>();
  spendingHistory.forEach(h => {
    h.detalhes.forEach(d => {
      averageByResourceMap.set(d.resource, (averageByResourceMap.get(d.resource) || 0) + d.cost);
    });
  });

  const totalSumCosts = Array.from(averageByResourceMap.values()).reduce((acc, val) => acc + val, 0);
  const pieData = Array.from(averageByResourceMap.entries()).map(([name, sumValue], index) => {
    const avgValue = spendingHistory.length > 0 ? sumValue / spendingHistory.length : 0;
    const percent = totalSumCosts > 0 ? Math.round((sumValue / totalSumCosts) * 1000) / 10 : 0;
    return {
      name,
      percent,
      value: avgValue,
      color: pieColors[index % pieColors.length]
    };
  });
  let accumulatedPercent = 0;
  const donutCircumference = 314.16;

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 24, color: '#374151' }}>

      {/* CSS para animação de pulsação e efeitos */}
      <style>{`
        @keyframes heartbeat {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 0.8; }
        }
        .pulse-heart {
          animation: heartbeat 2s infinite ease-in-out;
        }
        .telemetry-table th, .telemetry-table td, .telemetry-table span, .telemetry-table select {
          font-size: 10px !important;
        }
        .hover-row .delta-text {
          opacity: 0;
          transform: translateY(-2px);
          transition: opacity 0.18s cubic-bezier(0.4, 0, 0.2, 1), transform 0.18s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .hover-row:hover .delta-text {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>

      {/* Cabeçalho */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, margin: 0, color: '#111827', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Activity size={28} style={{ color: '#4f46e5' }} />
            Cockpit de Saúde e Telemetria Operacional
          </h1>
          <p style={{ color: '#6b7280', margin: '4px 0 0 0', fontSize: '.85rem' }}>
            Monitore o estado de conexão, acessos de usuários, faturas de gastos na nuvem e o status de e-mails em tempo real.
          </p>
        </div>
        <button 
          onClick={fetchTelemetry} 
          style={{
            background: '#fff', border: '1px solid #d1d5db',
            borderRadius: 8, padding: '8px 16px', color: '#374151', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', fontWeight: 600,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'background .2s'
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
        >
          <RefreshCw size={14} />
          Atualizar Dados
        </button>
      </div>

      {/* Seletor de Ambiente (Todos / Produção / Teste) */}
      <div style={{
        display: 'flex', gap: 8, background: '#1f2937', padding: 4, borderRadius: 8, width: 'fit-content',
        border: '1px solid rgba(255,255,255,0.05)', alignSelf: 'flex-start'
      }}>
        {[
          { key: 'todos', label: 'Todos os Ambientes' },
          { key: 'prod', label: 'Produção (alunoconectado.com.br)' },
          { key: 'test', label: 'Homologação (test.alunoconectado.com.br)' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key as any)}
            style={{
              padding: '6px 14px', borderRadius: 6, fontSize: '.78rem', fontWeight: 600, border: 'none',
              cursor: 'pointer', transition: 'all 0.2s',
              background: activeTab === tab.key ? '#374151' : 'transparent',
              color: activeTab === tab.key ? '#818cf8' : 'rgba(255,255,255,0.5)',
              boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.2)' : 'none'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* HERO: Visão Geral de Saúde */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
        border: '1px solid rgba(99,102,241,0.2)', borderRadius: 16, padding: '28px 32px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff',
        boxShadow: '0 4px 20px rgba(49,46,129,0.15)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{
            background: 'rgba(16,185,129,0.15)',
            border: '2px solid rgba(16,185,129,0.4)',
            width: 60, height: 60, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'center'
          }} className="pulse-heart">
            <ShieldCheck size={36} style={{ color: '#10b981' }} />
          </div>
          <div>
            <span style={{ fontSize: '.75rem', fontWeight: 800, color: '#a5b4fc', textTransform: 'uppercase', letterSpacing: '.05em' }}>Status Geral dos Serviços</span>
            <h2 style={{ fontSize: '1.8rem', fontWeight: 800, margin: '4px 0 0 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              Sistemas 100% Saudáveis
            </h2>
            <p style={{ color: '#c7d2fe', fontSize: '.82rem', margin: '4px 0 0 0' }}>
              Firebase, Cloud Run, Cloud SQL e servidores SMTP operando com latência ideal.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 32, paddingRight: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '.7rem', color: '#a5b4fc', textTransform: 'uppercase', display: 'block' }}>Uptime Médio</span>
            <span style={{ fontSize: '1.6rem', fontWeight: 800, fontFamily: 'monospace' }}>{data.uptime}</span>
          </div>
          <div style={{ textAlign: 'right', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: 32 }}>
            <span style={{ fontSize: '.7rem', color: '#a5b4fc', textTransform: 'uppercase', display: 'block' }}>Gastos no Mês</span>
            <span style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fbbf24' }}>R$ {formatBRL(currentGasto)}</span>
          </div>
        </div>
      </div>

      {/* QUADRO DE CUSTO OPERACIONAL (FinOps) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 24 }}>

        {/* Cartões e Alerta de Orçamento */}
        <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 16, padding: 24, color: '#fff', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', margin: 0, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                <DollarSign size={18} style={{ color: '#fbbf24' }} />
                Quadro de Custos Operacionais
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.4)', margin: '4px 0 0 0', fontSize: '.78rem' }}>
                Gestão financeira (FinOps) de recursos em nuvem no período atual.
              </p>
            </div>

            <button
              onClick={() => setBudgetModalOpen(true)}
              style={{
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
                color: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: '.75rem', fontWeight: 600,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'background .2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
            >
              <Sliders size={14} />
              Configurar
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 14 }}>
              <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Fatura GCP Anterior</span>
              <div style={{ fontSize: '1.25rem', fontWeight: 800, marginTop: 4 }}>
                R$ {formatBRL(data.custos.ultimaFatura * (activeTab === 'prod' ? 0.75 : activeTab === 'test' ? 0.25 : 1))}
              </div>
              <span style={{ fontSize: '.68rem', color: '#34d399', marginTop: 4, display: 'block' }}>✓ Paga em 01/07</span>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 14 }}>
              <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Créditos Disponíveis</span>
              <div style={{ fontSize: '1.25rem', fontWeight: 800, marginTop: 4, color: '#34d399' }}>
                R$ {formatBRL((data.custos.creditosDisponiveis || 0) * (activeTab === 'prod' ? 0.75 : activeTab === 'test' ? 0.25 : 1))}
              </div>
              <span style={{ fontSize: '.68rem', color: 'rgba(255,255,255,0.4)', marginTop: 4, display: 'block' }}>Abatido no faturamento</span>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 14 }}>
              <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Alerta de Orçamento</span>
              <div style={{ fontSize: '1.25rem', fontWeight: 800, marginTop: 4 }}>R$ {formatBRL(data.custos.limiteOrcamento)}</div>
              <span style={{ fontSize: '.68rem', color: '#fbbf24', marginTop: 4, display: 'block' }}>
                {data.custos.limitesConfigurados.length} barreiras ativas
              </span>
            </div>
          </div>

          {/* Barra de Progresso do Orçamento */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.75rem', fontWeight: 600 }}>
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>Consumo do Limite de Alerta ({data.custos.limiteOrcamento} BRL)</span>
              <span style={{ color: '#fbbf24' }}>{percentOrcamento.toFixed(1)}%</span>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 99, height: 8, overflow: 'hidden' }}>
              <div style={{
                background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)',
                width: `${percentOrcamento}%`, height: '100%', transition: 'width 0.4s ease'
              }} />
            </div>
          </div>
        </div>

        {/* Divisão de Gastos por Recurso */}
        <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 16, padding: 24, color: '#fff', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', margin: 0, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileBarChart size={18} style={{ color: '#818cf8' }} />
                Gastos por Recurso
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.4)', margin: '4px 0 0 0', fontSize: '.78rem' }}>
                Divisão proporcional dos custos no faturamento atual.
              </p>
            </div>

            <button
              onClick={handleOpenSpendingHistory}
              style={{
                background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)',
                color: '#c7d2fe', borderRadius: 8, padding: '6px 12px', fontSize: '.75rem', fontWeight: 600,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'background .2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(99,102,241,0.15)'}
            >
              <Clock size={14} />
              Histórico
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 4 }}>
            {data.custos.porRecurso.map(r => {
              const custoFiltrado = r.custo * fatorCusto;
              return (
                <div key={r.recurso} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.73rem' }}>
                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>{r.recurso}</span>
                    <span style={{ fontWeight: 700 }}>R$ {formatBRL(custoFiltrado)} ({r.porcentagem}%)</span>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 99, height: 4, overflow: 'hidden' }}>
                    <div style={{
                      background: '#6366f1',
                      width: `${r.porcentagem}%`, height: '100%'
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* METRICAS DE TRÁFEGO E USO */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

        {/* Acessos Diários */}
        <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 16, padding: 24, color: '#fff' }}>
          <h3 style={{ fontSize: '1.05rem', margin: '0 0 16px 0', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
            <TrendingUp size={18} style={{ color: '#34d399' }} />
            Volume de Acessos Diários (Últimos 7 dias)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.acessos.porDia.map(d => {
              const acessosFiltrados = Math.floor(d.acessos * fatorTrafego);
              return (
                <div key={d.dia} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 80, fontSize: '.78rem', color: 'rgba(255,255,255,0.6)' }}>{d.dia}</span>
                  <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 16, overflow: 'hidden', position: 'relative' }}>
                    <div style={{
                      background: 'rgba(52,211,153,0.2)', borderRight: '2px solid #34d399',
                      width: `${(acessosFiltrados / 5000) * 100}%`, height: '100%'
                    }} />
                    <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: '.7rem', fontWeight: 700 }}>
                      {acessosFiltrados} acessos
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Histograma de Horário de Pico */}
        <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 16, padding: 24, color: '#fff' }}>
          <h3 style={{ fontSize: '1.05rem', margin: '0 0 16px 0', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={18} style={{ color: '#60a5fa' }} />
            Distribuição de Tráfego por Horário (Dia Atual)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.acessos.porHora.map(h => {
              const acessosFiltrados = Math.floor(h.acessos * fatorTrafego);
              return (
                <div key={h.hora} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 60, fontSize: '.78rem', color: 'rgba(255,255,255,0.6)' }}>{h.hora}</span>
                  <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 16, overflow: 'hidden', position: 'relative' }}>
                    <div style={{
                      background: 'rgba(96,165,250,0.2)', borderRight: '2px solid #60a5fa',
                      width: `${(acessosFiltrados / 1500) * 100}%`, height: '100%'
                    }} />
                    <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: '.7rem', fontWeight: 700 }}>
                      {acessosFiltrados} acessos/hora
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* PÁGINAS MAIS ACESSADAS E USUÁRIOS MAIS ATIVOS */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 24 }}>

        {/* Páginas Mais Acessadas */}
        <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 16, padding: 20, color: '#fff' }}>
          <h3 style={{ fontSize: '1.05rem', margin: '0 0 14px 0', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={18} style={{ color: '#a78bfa' }} />
            Páginas Mais Visitadas (Últimos 30 dias)
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}>
                <th style={{ padding: 10 }}>Página</th>
                <th style={{ padding: 10 }}>Caminho de Acesso (URL)</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Total de Acessos</th>
              </tr>
            </thead>
            <tbody>
              {data.acessos.paginasMaisAcessadas.map(p => {
                const acessosFiltrados = Math.floor(p.acessos * fatorTrafego);
                return (
                  <tr key={p.url} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                    <td style={{ padding: 10, fontWeight: 600 }}>{p.nome}</td>
                    <td style={{ padding: 10, fontFamily: 'monospace', color: 'rgba(255,255,255,0.6)' }}>{p.url}</td>
                    <td style={{ padding: 10, textAlign: 'right', fontWeight: 700, color: '#c7d2fe' }}>{acessosFiltrados.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Usuários Mais Ativos */}
        <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 16, padding: 20, color: '#fff' }}>
          <h3 style={{ fontSize: '1.05rem', margin: '0 0 14px 0', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Users size={18} style={{ color: '#f472b6' }} />
            Operadores mais Ativos (Ações Administrativas)
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}>
                <th style={{ padding: 10 }}>Nome</th>
                <th style={{ padding: 10 }}>E-mail</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {data.acessos.usuariosMaisAtivos.map(u => {
                const acessosFiltrados = Math.floor(u.acessos * fatorTrafego);
                return (
                  <tr key={u.email} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                    <td style={{ padding: 10, fontWeight: 600 }}>{u.name}</td>
                    <td style={{ padding: 10, color: 'rgba(255,255,255,0.6)' }}>{u.email}</td>
                    <td style={{ padding: 10, textAlign: 'right', fontWeight: 700, color: '#fbcfe8' }}>{acessosFiltrados}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      </div>

      {/* INTEGRAÇÃO SMTP & EMAIL (SendGrid) */}
      <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 16, padding: 24, color: '#fff', display: 'flex', flexDirection: 'column', gap: 20 }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', margin: 0, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Send size={18} style={{ color: '#60a5fa' }} />
              Saúde do Envio de E-mails (SendGrid)
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.4)', margin: '4px 0 0 0', fontSize: '.78rem' }}>
              Estatísticas e logs de falhas técnicas SMTP de mensagens automáticas do sistema.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{
              background: 'rgba(16,185,129,0.15)', color: '#34d399',
              border: '1px solid rgba(16,185,129,0.3)', borderRadius: 6,
              padding: '6px 12px', fontSize: '.75rem', fontWeight: 700
            }}>
              Serviço: {data.sendGrid.status}
            </span>
          </div>
        </div>

        {/* Indicadores do SendGrid & Teste SMTP */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 24 }}>

          {/* Cota e Envio */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 16 }}>
              <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Cota Mensal Consumida</span>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, marginTop: 4, color: '#6366f1' }}>
                {Math.floor(data.sendGrid.enviados * (activeTab === 'prod' ? 0.75 : activeTab === 'test' ? 0.25 : 1)).toLocaleString()} / {data.sendGrid.limiteMensal.toLocaleString()}
              </div>
              <span style={{ fontSize: '.68rem', color: 'rgba(255,255,255,0.4)', marginTop: 4, display: 'block' }}>
                Taxa de Entrega: <strong>{data.sendGrid.deliveryRate}</strong>
              </span>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 16 }}>
              <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Endereços com Falhas Ativas</span>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, marginTop: 4, color: '#f87171' }}>{totalFalhasFiltradas}</div>
              <span style={{ fontSize: '.68rem', color: '#fca5a5', marginTop: 4, display: 'block' }}>Envios suspensos por segurança</span>
            </div>
          </div>

          {/* Form de Envio de Teste SMTP */}
          <form onSubmit={handleSendTestEmail} style={{
            background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
            borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10
          }}>
            <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', fontWeight: 700 }}>Testar Conectividade SMTP</span>
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                type="email"
                value={testEmailAddress}
                onChange={e => setTestEmailAddress(e.target.value)}
                placeholder="Ex: administrador@escola.com"
                style={{
                  flex: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: '.8rem', outline: 'none'
                }}
              />
              <button
                type="submit"
                disabled={sendingTest}
                style={{
                  background: '#6366f1', border: 'none', borderRadius: 6, padding: '8px 16px',
                  color: '#fff', cursor: 'pointer', fontSize: '.8rem', fontWeight: 700,
                  display: 'flex', alignItems: 'center', gap: 6
                }}
              >
                {sendingTest ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                Testar Envio
              </button>
            </div>
            <span style={{ fontSize: '.65rem', color: 'rgba(255,255,255,0.3)' }}>
              Escreva <strong>erro-inexistente</strong> ou <strong>erro-cheio</strong> no e-mail para simular falhas e bouncers da nuvem.
            </span>
          </form>

        </div>

        {/* Tabela de logs ativos agrupados */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <AlertTriangle size={14} style={{ color: '#fbbf24' }} />
            <span style={{ fontSize: '.78rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}>E-mails Ativos com Falhas Técnicas de Entrega ({totalFalhasFiltradas})</span>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }}>
                <th style={{ padding: 10 }}>Destinatário (E-mail)</th>
                <th style={{ padding: 10 }}>Situação</th>
                <th style={{ padding: 10, textAlign: 'center' }}>Falhas</th>
                <th style={{ padding: 10 }}>Último Assunto / Erro Técnico</th>
                <th style={{ padding: 10 }}>Data Última Falha</th>
                <th style={{ padding: 10, textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {logsFalhasFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 20, textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>
                    Nenhuma falha de entrega ativa registrada no momento.
                  </td>
                </tr>
              ) : logsFalhasFiltrados.map(log => (
                <tr key={log.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                  <td style={{ padding: 10, fontWeight: 700, color: '#fff' }}>{log.email}</td>
                  <td style={{ padding: 10 }}>
                    {log.situacao === 'BLOQUEADO' ? (
                      <span style={{ background: 'rgba(239,68,68,0.2)', color: '#fca5a5', padding: '2px 6px', borderRadius: 4, fontSize: '.68rem', fontWeight: 700 }}>Bloqueado (Inexistente)</span>
                    ) : log.situacao === 'AVISO_RISCO' ? (
                      <span style={{ background: 'rgba(249,115,22,0.2)', color: '#ffedd5', padding: '2px 6px', borderRadius: 4, fontSize: '.68rem', fontWeight: 700 }}>Risco de Perda de Acesso</span>
                    ) : (
                      <span style={{ background: 'rgba(234,179,8,0.2)', color: '#fef9c3', padding: '2px 6px', borderRadius: 4, fontSize: '.68rem', fontWeight: 700 }}>Instabilidade Temporária</span>
                    )}
                  </td>
                  <td style={{ padding: 10, textAlign: 'center', fontWeight: 700, color: '#f87171' }}>{log.contadorFalhas}x</td>
                  <td style={{ padding: 10 }}>
                    <div style={{ color: 'rgba(255,255,255,0.85)' }}>{log.assunto}</div>
                    <div style={{ fontSize: '.68rem', color: '#fca5a5', fontFamily: 'monospace', marginTop: 2 }}>{log.erro}</div>
                  </td>
                  <td style={{ padding: 10, color: 'rgba(255,255,255,0.4)' }}>{new Date(log.timestamp).toLocaleString()}</td>
                  <td style={{ padding: 10, textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 8 }}>
                      <button
                        onClick={() => handleOpenEmailDetails(log.email)}
                        style={{
                          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 4, padding: '4px 8px', color: '#cbd5e1', cursor: 'pointer',
                          fontSize: '.7rem', fontWeight: 600
                        }}
                      >
                        Ver Detalhes
                      </button>
                      <button
                        onClick={() => handleClearFailures(log.email)}
                        style={{
                          background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
                          borderRadius: 4, padding: '4px 8px', color: '#a7f3d0', cursor: 'pointer',
                          fontSize: '.7rem', fontWeight: 700
                        }}
                      >
                        Zerar Falhas
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Seção Histórico de Falhas Arquivadas */}
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HardDrive size={16} style={{ color: 'rgba(255,255,255,0.4)' }} />
            <span style={{ fontSize: '.75rem', color: 'rgba(255,255,255,0.5)' }}>
              Temos <strong>{data.sendGrid.totalHistorico} falhas arquivadas</strong> arquivadas no histórico permanente.
            </span>
          </div>
          <button
            onClick={handleOpenEmailHistory}
            style={{
              background: 'none', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 6, padding: '6px 12px', color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
              fontSize: '.73rem', fontWeight: 600, transition: 'all .2s'
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#fff'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
          >
            Navegar pelo Histórico de Falhas
          </button>
        </div>

      </div>

      {/* DETALHAMENTO DA INFRAESTRUTURA */}
      <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 16, padding: 24, color: '#fff', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <h3 style={{ fontSize: '1.05rem', margin: 0, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Server size={18} style={{ color: '#cbd5e1' }} />
            Saúde dos Servidores de Infraestrutura (GCP & Firebase)
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.4)', margin: '4px 0 0 0', fontSize: '.78rem' }}>
            Estado de conexões de nuvem do portal do Aluno Conectado.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>

          {/* Firebase Hosting */}
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.4)' }}>Firebase Hosting</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ background: '#10b981', width: 8, height: 8, borderRadius: '50%', display: 'inline-block' }} />
              <strong style={{ fontSize: '.85rem' }}>{data.infraestrutura.firebaseHosting.status}</strong>
            </div>
            <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)' }}>
              Host: {activeTab === 'test' ? 'test.alunoconectado.com.br' : 'alunoconectado.com.br'} | Latência: {data.infraestrutura.firebaseHosting.latencia}
            </span>
          </div>

          {/* Firebase Auth */}
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.4)' }}>Firebase Auth</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ background: '#10b981', width: 8, height: 8, borderRadius: '50%', display: 'inline-block' }} />
              <strong style={{ fontSize: '.85rem' }}>{data.infraestrutura.firebaseAuth.status}</strong>
            </div>
            <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)' }}>Latência: {data.infraestrutura.firebaseAuth.latencia}</span>
          </div>

          {/* Firebase Storage */}
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.4)' }}>Firebase Storage</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ background: '#10b981', width: 8, height: 8, borderRadius: '50%', display: 'inline-block' }} />
              <strong style={{ fontSize: '.85rem' }}>{data.infraestrutura.firebaseStorage.status}</strong>
            </div>
            <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)' }}>Latência: {data.infraestrutura.firebaseStorage.latencia}</span>
          </div>

          {/* Google Cloud Run */}
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.4)' }}>GCP Cloud Run</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ background: '#10b981', width: 8, height: 8, borderRadius: '50%', display: 'inline-block' }} />
              <strong style={{ fontSize: '.85rem' }}>{data.infraestrutura.cloudRun.status}</strong>
            </div>
            <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)' }}>
              Serviço: {activeTab === 'test' ? 'alunoconectado-api (Stg)' : 'alunoconectado-api-prod'} | CPU: {activeTab === 'test' ? '4%' : '12%'}
            </span>
          </div>

          {/* Google Cloud SQL */}
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: '.72rem', color: 'rgba(255,255,255,0.4)' }}>GCP Cloud SQL</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ background: '#10b981', width: 8, height: 8, borderRadius: '50%', display: 'inline-block' }} />
              <strong style={{ fontSize: '.85rem' }}>{data.infraestrutura.cloudSql.status}</strong>
            </div>
            <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)' }}>
              Instância: {activeTab === 'test' ? 'escola-db' : 'escola-db-prod'} | CPU: {activeTab === 'test' ? '2%' : '8%'}
            </span>
          </div>

        </div>

      </div>


      {/* PAINEL DE GOVERNANÇA DE RECURSOS & FINOPS (GOOGLE CLOUD) */}
      {cloudStats && (
        <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 16, padding: 24, color: '#fff', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: '1.05rem', margin: 0, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                <HardDrive size={18} style={{ color: '#818cf8' }} />
                Governança de Créditos & Otimização Cloud (FinOps)
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.4)', margin: '4px 0 0 0', fontSize: '.78rem' }}>
                Monitore e libere créditos consumidos por revisões órfãs do Cloud Run e imagens antigas de build do GCR.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => {
                  setCloudDetailsActiveTab('revisions');
                  setCloudDetailsModalOpen(true);
                }}
                style={{
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff', borderRadius: 8, padding: '10px 18px', fontSize: '.8rem', fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all .2s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
              >
                <Eye size={14} />
                Ver Detalhes do Cloud
              </button>

              <button
                onClick={handleCloudCleanup}
                disabled={cleaningCloud || loadingCloudStats}
                style={{
                  background: '#6366f1', border: 'none',
                  color: '#fff', borderRadius: 8, padding: '10px 18px', fontSize: '.8rem', fontWeight: 700,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'background .2s',
                  opacity: (cleaningCloud || loadingCloudStats) ? 0.6 : 1
                }}
              >
                {cleaningCloud ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Limpando Nuvem...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} />
                    Executar Limpeza Geral
                  </>
                )}
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {/* Imagens Docker */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Imagens Docker (GCR)</span>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, marginTop: 4, color: '#818cf8' }}>
                {activeTab === 'todos' ? cloudStats.images.total : activeTab === 'prod' ? cloudStats.images.production : cloudStats.images.staging}
              </div>
              <span style={{ fontSize: '.68rem', color: 'rgba(255,255,255,0.4)' }}>
                Espaço: <strong>{activeTab === 'todos' ? cloudStats.images.estimatedSizeGb : activeTab === 'prod' ? (cloudStats.images.production * 0.82).toFixed(1) : (cloudStats.images.staging * 0.82).toFixed(1)} GB</strong>
              </span>
            </div>

            {/* Revisões Cloud Run */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Revisões Cloud Run</span>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, marginTop: 4, color: '#34d399' }}>
                {activeTab === 'todos' ? cloudStats.revisions.total : activeTab === 'prod' ? cloudStats.revisions.production : cloudStats.revisions.staging}
              </div>
              <span style={{ fontSize: '.68rem', color: 'rgba(255,255,255,0.4)' }}>
                Staging: <strong>{cloudStats.revisions.staging}</strong> | Prod: <strong>{cloudStats.revisions.production}</strong>
              </span>
            </div>

            {/* Backups Cloud SQL */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Backups Retidos</span>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, marginTop: 4, color: '#3b82f6' }}>
                {activeTab === 'todos' ? cloudStats.backups.total : activeTab === 'prod' ? cloudStats.backups.production : cloudStats.backups.staging}
              </div>
              <span style={{ fontSize: '.68rem', color: 'rgba(255,255,255,0.4)' }}>
                Staging: <strong>{cloudStats.backups.staging}</strong> | Prod: <strong>{cloudStats.backups.production}</strong>
              </span>
            </div>

            {/* VM Emuladores & Discos */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>VM Emuladores ociosa</span>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, marginTop: 4, color: cloudStats.vms.emuladoresRunner === 'DELETED' ? '#10b981' : '#f59e0b' }}>
                {cloudStats.vms.emuladoresRunner === 'DELETED' ? 'DESATIVADA' : cloudStats.vms.emuladoresRunner}
              </div>
              <span style={{ fontSize: '.68rem', color: cloudStats.vms.emuladoresRunner === 'DELETED' ? '#34d399' : '#fbbf24' }}>
                {cloudStats.vms.emuladoresRunner === 'DELETED' ? '✓ Economia garantida' : `Aviso: disco permanent de ${cloudStats.vms.activeDisksGb}GB ativo`}
              </span>
            </div>
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '.75rem', color: 'rgba(255,255,255,0.5)' }}>
              Economia estimada neste mês com governança de limpeza: <strong style={{ color: '#34d399' }}>R$ {(cloudStats.costs.savingsThisMonth * (activeTab === 'prod' ? 0.75 : activeTab === 'test' ? 0.25 : 1)).toFixed(2)}</strong>
            </span>
            <span style={{ fontSize: '.7rem', color: 'rgba(255,255,255,0.4)' }}>
              Última auditoria: {new Date(cloudStats.lastCleanup || Date.now()).toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* MODAL 5: DETALHES DE RECURSOS CLOUD (FINOPS INTERATIVO) */}
      {cloudDetailsModalOpen && cloudStats && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#111827', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16, padding: 24, width: '100%', maxWidth: 950, color: '#fff',
            display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '85vh'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <HardDrive size={22} style={{ color: '#818cf8' }} />
                Gerenciador de Recursos Cloud - FinOps
                <span style={{ fontSize: '.85rem', color: '#a5b4fc', fontWeight: 600, background: 'rgba(165,180,252,0.1)', padding: '2px 8px', borderRadius: 6, marginLeft: 8 }}>
                  {activeTab === 'todos' ? 'Todos os Ambientes' : activeTab === 'prod' ? 'Produção' : 'Homologação'}
                </span>
              </h3>
              <button
                onClick={() => setCloudDetailsModalOpen(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ fontSize: '.8rem', color: 'rgba(255,255,255,0.5)', margin: 0 }}>
              Gerencie individualmente as revisões implantadas, tags de contêineres e logs de backup do Google Cloud.
            </p>

            {/* Abas internas do Modal */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 12 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { key: 'revisions', label: 'Revisões Cloud Run', count: (cloudStats.details?.revisions || []).filter((r: any) => activeTab === 'todos' || (activeTab === 'prod' ? r.service === 'alunoconectado-api-prod' : r.service === 'alunoconectado-api')).length },
                  { key: 'images', label: 'Imagens Docker (GCR)', count: (cloudStats.details?.images || []).filter((i: any) => activeTab === 'todos' || (activeTab === 'prod' ? i.repo === 'alunoconectado-api-prod' : i.repo === 'alunoconectado-api')).length },
                  { key: 'backups', label: 'Backups SQL', count: (cloudStats.details?.backups || []).filter((b: any) => activeTab === 'todos' || (activeTab === 'prod' ? b.instance === 'escola-db-prod' : b.instance === 'escola-db')).length }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setCloudDetailsActiveTab(tab.key as any);
                      setCloudSearchQuery('');
                    }}
                    style={{
                      background: 'none', border: 'none', padding: '8px 16px', fontSize: '.8rem', fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.2s',
                      color: cloudDetailsActiveTab === tab.key ? '#818cf8' : 'rgba(255,255,255,0.4)',
                      borderBottom: cloudDetailsActiveTab === tab.key ? '2px solid #818cf8' : 'none'
                    }}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </div>

              {/* Caixa de busca */}
              <input
                type="text"
                placeholder="Pesquisar recurso..."
                value={cloudSearchQuery}
                onChange={e => setCloudSearchQuery(e.target.value)}
                style={{
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 6, padding: '6px 12px', color: '#fff', fontSize: '.78rem', width: 220, outline: 'none'
                }}
              />
            </div>

            {/* Listagem do Modal */}
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, background: 'rgba(0,0,0,0.15)' }}>
              {cloudDetailsActiveTab === 'revisions' && (() => {
                const list = (cloudStats.details?.revisions || []).filter((r: any) => {
                  const matchesSearch = r.name.toLowerCase().includes(cloudSearchQuery.toLowerCase()) || r.author.toLowerCase().includes(cloudSearchQuery.toLowerCase());
                  const matchesTab = activeTab === 'todos' || (activeTab === 'prod' ? r.service === 'alunoconectado-api-prod' : r.service === 'alunoconectado-api');
                  return matchesSearch && matchesTab;
                });

                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem', textAlign: 'left' }}>
                    <thead style={{ background: 'rgba(255,255,255,0.02)', color: 'rgba(255,255,255,0.5)', position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr>
                        <th style={{ padding: '8px 12px' }}>Nome da Revisão</th>
                        <th style={{ padding: '8px 12px' }}>Serviço</th>
                        <th style={{ padding: '8px 12px' }}>Data de Criação</th>
                        <th style={{ padding: '8px 12px' }}>Autor</th>
                        <th style={{ padding: '8px 12px' }}>Estado</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.length === 0 ? (
                        <tr>
                          <td colSpan={6} style={{ padding: 24, color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
                            Nenhuma revisão encontrada.
                          </td>
                        </tr>
                      ) : list.map((r: any) => (
                        <tr key={r.name} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                          <td style={{ padding: '8px 12px', fontWeight: 600 }}>{r.name}</td>
                          <td style={{ padding: '8px 12px', color: '#a5b4fc', display: 'flex', alignItems: 'center', gap: 6 }}>
                            {r.service}
                            <span style={{
                              padding: '1px 5px', borderRadius: 4, fontSize: '.55rem', fontWeight: 700,
                              background: r.service.includes('prod') ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)',
                              color: r.service.includes('prod') ? '#f87171' : '#60a5fa'
                            }}>
                              {r.service.includes('prod') ? 'PROD' : 'STG'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px' }}>{new Date(r.createdAt).toLocaleString()}</td>
                          <td style={{ padding: '8px 12px', color: '#cbd5e1' }}>{r.author}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{
                              padding: '2px 6px', borderRadius: 4, fontSize: '.65rem', fontWeight: 700,
                              background: r.isActive ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.05)',
                              color: r.isActive ? '#10b981' : 'rgba(255,255,255,0.4)'
                            }}>
                              {r.isActive ? 'ATIVO (100% tráfego)' : 'INATIVO'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                            {!r.isActive ? (
                              <button
                                onClick={() => handleDeleteResource('revision', r.name)}
                                disabled={deletingResourceId === r.name}
                                style={{
                                  background: 'none', border: 'none', color: '#f87171', cursor: 'pointer',
                                  fontSize: '.75rem', fontWeight: 600, opacity: deletingResourceId === r.name ? 0.5 : 1
                                }}
                              >
                                {deletingResourceId === r.name ? 'Removendo...' : 'Remover'}
                              </button>
                            ) : (
                              <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '.7rem' }}>Protegido</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}

              {cloudDetailsActiveTab === 'images' && (() => {
                const list = (cloudStats.details?.images || []).filter((i: any) => {
                  const digestMatch = i.digest.toLowerCase().includes(cloudSearchQuery.toLowerCase());
                  const tagMatch = i.tags.some((t: string) => t.toLowerCase().includes(cloudSearchQuery.toLowerCase()));
                  const matchesSearch = digestMatch || tagMatch;
                  const matchesTab = activeTab === 'todos' || (activeTab === 'prod' ? i.repo === 'alunoconectado-api-prod' : i.repo === 'alunoconectado-api');
                  return matchesSearch && matchesTab;
                });

                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem', textAlign: 'left' }}>
                    <thead style={{ background: 'rgba(255,255,255,0.02)', color: 'rgba(255,255,255,0.5)', position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr>
                        <th style={{ padding: '8px 12px' }}>Digest (SHA256)</th>
                        <th style={{ padding: '8px 12px' }}>Tags</th>
                        <th style={{ padding: '8px 12px' }}>Repositório</th>
                        <th style={{ padding: '8px 12px' }}>Criado Em</th>
                        <th style={{ padding: '8px 12px' }}>Tamanho Est.</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.length === 0 ? (
                        <tr>
                          <td colSpan={6} style={{ padding: 24, color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
                            Nenhuma imagem Docker encontrada.
                          </td>
                        </tr>
                      ) : list.map((i: any) => {
                        const isLatest = i.tags.includes('latest') || i.tags.includes('prod-latest');
                        return (
                          <tr key={i.digest} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#94a3b8' }}>
                              {i.digest.slice(0, 24)}...
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              {i.tags.length === 0 ? (
                                <span style={{ color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>sem tag (órfã)</span>
                              ) : (
                                <div style={{ display: 'flex', gap: 4 }}>
                                  {i.tags.map((t: string) => (
                                    <span key={t} style={{
                                      background: 'rgba(99,102,241,0.1)', color: '#818cf8',
                                      padding: '2px 6px', borderRadius: 4, fontSize: '.65rem', fontWeight: 600
                                    }}>
                                      {t}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '8px 12px', color: '#a5b4fc' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {i.repo}
                                <span style={{
                                  padding: '1px 5px', borderRadius: 4, fontSize: '.55rem', fontWeight: 700,
                                  background: i.repo.includes('prod') ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)',
                                  color: i.repo.includes('prod') ? '#f87171' : '#60a5fa'
                                }}>
                                  {i.repo.includes('prod') ? 'PROD' : 'STG'}
                                </span>
                              </div>
                            </td>
                            <td style={{ padding: '8px 12px' }}>{new Date(i.createdAt).toLocaleString()}</td>
                            <td style={{ padding: '8px 12px' }}>{i.sizeGb} GB</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                              {!isLatest ? (
                                <button
                                  onClick={() => handleDeleteResource('image', i.digest, i.repo)}
                                  disabled={deletingResourceId === i.digest}
                                  style={{
                                    background: 'none', border: 'none', color: '#f87171', cursor: 'pointer',
                                    fontSize: '.75rem', fontWeight: 600, opacity: deletingResourceId === i.digest ? 0.5 : 1
                                  }}
                                >
                                  {deletingResourceId === i.digest ? 'Removendo...' : 'Remover'}
                                </button>
                              ) : (
                                <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '.7rem' }}>Protegida</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}

              {cloudDetailsActiveTab === 'backups' && (() => {
                const list = (cloudStats.details?.backups || []).filter((b: any) => {
                  const idMatch = b.id.includes(cloudSearchQuery);
                  const descMatch = b.description?.toLowerCase().includes(cloudSearchQuery.toLowerCase());
                  const matchesSearch = idMatch || descMatch;
                  const matchesTab = activeTab === 'todos' || (activeTab === 'prod' ? b.instance === 'escola-db-prod' : b.instance === 'escola-db');
                  return matchesSearch && matchesTab;
                });

                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem', textAlign: 'left' }}>
                    <thead style={{ background: 'rgba(255,255,255,0.02)', color: 'rgba(255,255,255,0.5)', position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr>
                        <th style={{ padding: '8px 12px' }}>ID do Backup</th>
                        <th style={{ padding: '8px 12px' }}>Instância SQL</th>
                        <th style={{ padding: '8px 12px' }}>Tipo</th>
                        <th style={{ padding: '8px 12px' }}>Concluído Em</th>
                        <th style={{ padding: '8px 12px' }}>Status</th>
                        <th style={{ padding: '8px 12px' }}>Descrição</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right' }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.length === 0 ? (
                        <tr>
                          <td colSpan={7} style={{ padding: 24, color: 'rgba(255,255,255,0.3)', textAlign: 'center' }}>
                            Nenhum backup retido encontrado.
                          </td>
                        </tr>
                      ) : list.map((b: any) => (
                        <tr key={b.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                          <td style={{ padding: '8px 12px', fontWeight: 600 }}>{b.id}</td>
                          <td style={{ padding: '8px 12px', color: '#a5b4fc' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {b.instance}
                              <span style={{
                                padding: '1px 5px', borderRadius: 4, fontSize: '.55rem', fontWeight: 700,
                                background: b.instance.includes('prod') ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)',
                                color: b.instance.includes('prod') ? '#f87171' : '#60a5fa'
                              }}>
                                {b.instance.includes('prod') ? 'PROD' : 'STG'}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{
                              padding: '2px 6px', borderRadius: 4, fontSize: '.6rem', fontWeight: 700,
                              background: b.type === 'AUTOMATIC' ? 'rgba(59,130,246,0.1)' : 'rgba(245,158,11,0.1)',
                              color: b.type === 'AUTOMATIC' ? '#3b82f6' : '#f59e0b'
                            }}>
                              {b.type}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px' }}>{new Date(b.endTime).toLocaleString()}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{
                              padding: '2px 6px', borderRadius: 4, fontSize: '.65rem', fontWeight: 700,
                              background: b.status === 'SUCCESSFUL' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                              color: b.status === 'SUCCESSFUL' ? '#10b981' : '#ef4444'
                            }}>
                              {b.status}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', color: 'rgba(255,255,255,0.6)' }}>{b.description}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                            {b.type === 'ON_DEMAND' ? (
                              <button
                                onClick={() => handleDeleteResource('backup', b.id, b.instance)}
                                disabled={deletingResourceId === b.id}
                                style={{
                                  background: 'none', border: 'none', color: '#f87171', cursor: 'pointer',
                                  fontSize: '.75rem', fontWeight: 600, opacity: deletingResourceId === b.id ? 0.5 : 1
                                }}
                              >
                                {deletingResourceId === b.id ? 'Removendo...' : 'Remover'}
                              </button>
                            ) : (
                              <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '.7rem' }}>Protegido</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
              <button
                onClick={() => setCloudDetailsModalOpen(false)}
                style={{
                  background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, padding: '10px 20px', color: '#fff', cursor: 'pointer', fontSize: '.85rem', fontWeight: 600
                }}
              >
                Fechar Painel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════ MODAIS DA PÁGINA ════════════════════════ */}

      {/* MODAL 1: CONFIGURAR LIMITES DE ORÇAMENTO */}
      {budgetModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#111827', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16, padding: 24, width: '100%', maxWidth: 520, color: '#fff',
            display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '80vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <DollarSign size={20} style={{ color: '#fbbf24' }} />
                Configurar Alertas de Orçamento GCP
              </h3>
              <button
                onClick={() => setBudgetModalOpen(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ fontSize: '.8rem', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
              Defina as barreiras de limite orçamentário. Quando o consumo de recursos na nuvem atingir o limite correspondente, e-mails de segurança e pushes FCM móveis serão enviados aos contatos administrativos vinculados.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
              {editBudgetLimits.map((limit, index) => (
                <div key={limit.id} style={{
                  background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 10
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '.75rem', fontWeight: 700, color: '#818cf8' }}>Limite #{index + 1}</span>
                    <button
                      onClick={() => handleRemoveBudgetLimit(limit.id)}
                      style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '.7rem' }}
                    >
                      <Trash2 size={12} /> Remover
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label style={{ fontSize: '.68rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Valor da Barreira (BRL)</label>
                      <input
                        type="number"
                        value={limit.valor}
                        onChange={e => {
                          const val = Number(e.target.value);
                          setEditBudgetLimits(prev => prev.map(l => l.id === limit.id ? { ...l, valor: val } : l));
                        }}
                        style={{
                          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 6, padding: '8px 10px', color: '#fff', fontSize: '.8rem', outline: 'none'
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label style={{ fontSize: '.68rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Notificar Administradores</label>
                      <input
                        type="text"
                        value={limit.notifiedAdmins.join(', ')}
                        onChange={e => {
                          const admins = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                          setEditBudgetLimits(prev => prev.map(l => l.id === limit.id ? { ...l, notifiedAdmins: admins } : l));
                        }}
                        placeholder="Ex: euler@ac.br, marcelo@ac.br"
                        style={{
                          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 6, padding: '8px 10px', color: '#fff', fontSize: '.8rem', outline: 'none'
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={handleAddBudgetLimit}
                style={{
                  background: 'none', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: 10,
                  padding: 12, color: '#c7d2fe', cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 6, fontSize: '.8rem', fontWeight: 600, marginTop: 4
                }}
              >
                <Plus size={14} /> Adicionar Novo Limite
              </button>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              <button
                onClick={() => setBudgetModalOpen(false)}
                style={{
                  flex: 1, background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, padding: 12, color: '#fff', cursor: 'pointer', fontSize: '.85rem', fontWeight: 600
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveBudgetLimits}
                style={{
                  flex: 1, background: '#6366f1', border: 'none',
                  borderRadius: 8, padding: 12, color: '#fff', cursor: 'pointer', fontSize: '.85rem', fontWeight: 700
                }}
              >
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}


      {/* MODAL 2: HISTÓRICO COMPLETO DE ERROS DE UM E-MAIL ESPECÍFICO */}
      {emailDetailsModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#111827', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16, padding: 24, width: '100%', maxWidth: 650, color: '#fff',
            display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '85vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: 0, color: '#fff' }}>
                  Logs Históricos: {selectedEmailAddress}
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.4)', margin: '4px 0 0 0', fontSize: '.75rem' }}>
                  Histórico unificado de falhas registradas de comunicação deste destinatário.
                </p>
              </div>
              <button
                onClick={() => {
                  setEmailDetailsModalOpen(false);
                  setSelectedEmailLogs([]);
                }}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            {loadingEmailDetails ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 180, gap: 10 }}>
                <Loader2 size={24} className="animate-spin" style={{ color: '#6366f1' }} />
                <span style={{ fontSize: '.75rem', color: 'rgba(255,255,255,0.5)' }}>Carregando histórico...</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {selectedEmailLogs.map((log, index) => (
                  <div key={log.id || index} style={{
                    background: log.status === 'ARQUIVADO' ? 'rgba(255,255,255,0.01)' : 'rgba(239,68,68,0.02)',
                    border: log.status === 'ARQUIVADO' ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(239,68,68,0.15)',
                    borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 8
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.72rem' }}>
                      <span style={{ color: '#818cf8', fontWeight: 700 }}>
                        Ação #{selectedEmailLogs.length - index}
                        {log.status === 'ARQUIVADO' && <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: 8 }}>(Arquivado)</span>}
                      </span>
                      <span style={{ color: 'rgba(255,255,255,0.4)' }}>{new Date(log.timestamp).toLocaleString()}</span>
                    </div>

                    <div style={{ fontSize: '.8rem' }}>
                      <strong>Assunto:</strong> {log.assunto}
                    </div>

                    <div style={{ fontSize: '.8rem', background: 'rgba(0,0,0,0.2)', padding: '6px 10px', borderRadius: 6, fontFamily: 'monospace', color: '#fca5a5' }}>
                      Erro SMTP: {log.erro}
                    </div>

                    <div style={{
                      background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.12)',
                      borderRadius: 8, padding: 10, fontSize: '.75rem', color: '#a7f3d0'
                    }}>
                      <strong>Recomendação:</strong> {log.recomendacao}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {/* MODAL 3: HISTÓRICO GERAL DE ERROS ARQUIVADOS */}
      {emailHistoryModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#111827', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16, padding: 24, width: '100%', maxWidth: 700, color: '#fff',
            display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '85vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: 0, color: '#fff' }}>
                  Histórico Permanente de Falhas SMTP (Limpos/Arquivados)
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.4)', margin: '4px 0 0 0', fontSize: '.75rem' }}>
                  Registro de erros históricos de e-mails cuja falha foi zerada pelo operador.
                </p>
              </div>
              <button
                onClick={() => setEmailHistoryModalOpen(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            {loadingEmailHistory ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 10 }}>
                <Loader2 size={24} className="animate-spin" style={{ color: '#6366f1' }} />
                <span style={{ fontSize: '.75rem', color: 'rgba(255,255,255,0.5)' }}>Carregando registros...</span>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }}>
                    <th style={{ padding: 10 }}>Destinatário (E-mail)</th>
                    <th style={{ padding: 10 }}>Assunto</th>
                    <th style={{ padding: 10 }}>Erro Registrado</th>
                    <th style={{ padding: 10 }}>Data de Arquivamento</th>
                  </tr>
                </thead>
                <tbody>
                  {emailHistoryLogs.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: 20, textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>
                        Nenhum registro no histórico permanente.
                      </td>
                    </tr>
                  ) : emailHistoryLogs.map((log, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                      <td style={{ padding: 10, fontWeight: 700 }}>{log.email}</td>
                      <td style={{ padding: 10, color: 'rgba(255,255,255,0.8)' }}>{log.assunto}</td>
                      <td style={{ padding: 10, color: '#fca5a5', fontFamily: 'monospace' }}>{log.erro}</td>
                      <td style={{ padding: 10, color: 'rgba(255,255,255,0.4)' }}>{new Date(log.timestamp).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}


      {/* MODAL 4: HISTÓRICO DE GASTOS COMPLETO (Largura/Altura total, margem de 30px, split de fatias e anos colapsados) */}
      {spendingHistoryModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            position: 'absolute', top: 30, left: 30, right: 30, bottom: 30,
            background: '#111827', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 16, padding: 28, color: '#fff',
            display: 'flex', flexDirection: 'column', gap: 20, overflow: 'hidden'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 12, flexShrink: 0 }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileBarChart size={22} style={{ color: '#818cf8' }} />
                  Histórico Financeiro GCP & SMTP
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.4)', margin: '4px 0 0 0', fontSize: '.8rem' }}>
                  Acompanhe e analise a evolução dos gastos mensais com os recursos da nuvem e IA.
                </p>
              </div>

              {/* Controles do Header: Toggle de Modo + Dropdown de Exportação */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                {/* Toggle de Visão */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 2, background: 'rgba(255,255,255,0.04)', padding: 3, borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)' }}>
                  <button
                    onClick={() => setViewMode('mom')}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 6,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      background: viewMode === 'mom' ? '#374151' : 'transparent',
                      color: viewMode === 'mom' ? '#ffffff' : 'rgba(255,255,255,0.5)',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    Mês a Mês (MoM)
                  </button>
                  <button
                    onClick={() => setViewMode('accumulated')}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 6,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      background: viewMode === 'accumulated' ? '#374151' : 'transparent',
                      color: viewMode === 'accumulated' ? '#ffffff' : 'rgba(255,255,255,0.5)',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    Acumulado
                  </button>
                </div>

                {/* Dropdown de Exportação */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 12px',
                      borderRadius: 8,
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      background: '#4f46e5',
                      color: '#ffffff',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                  >
                    <Download size={14} />
                    <span>Exportar</span>
                    <ChevronDown size={12} />
                  </button>

                  {exportDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: 6,
                      background: '#1f2937',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      width: 170,
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
                      zIndex: 20,
                      padding: 4
                    }}>
                      <button
                        onClick={() => {
                          handleExport('csv');
                          setExportDropdownOpen(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          padding: '8px 10px',
                          background: 'none',
                          border: 'none',
                          color: 'rgba(255,255,255,0.8)',
                          textAlign: 'left',
                          fontSize: '0.7rem',
                          borderRadius: 6,
                          cursor: 'pointer'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <FileText size={14} style={{ color: '#fbbf24' }} />
                        Exportar para CSV
                      </button>
                      <button
                        onClick={() => {
                          handleExport('excel');
                          setExportDropdownOpen(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          padding: '8px 10px',
                          background: 'none',
                          border: 'none',
                          color: 'rgba(255,255,255,0.8)',
                          textAlign: 'left',
                          fontSize: '0.7rem',
                          borderRadius: 6,
                          cursor: 'pointer'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <Table size={14} style={{ color: '#10b981' }} />
                        Exportar para Excel
                      </button>
                      <button
                        onClick={() => {
                          handleExport('google');
                          setExportDropdownOpen(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          padding: '8px 10px',
                          background: 'none',
                          border: 'none',
                          color: 'rgba(255,255,255,0.8)',
                          textAlign: 'left',
                          fontSize: '0.7rem',
                          borderRadius: 6,
                          cursor: 'pointer'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <Globe size={14} style={{ color: '#3b82f6' }} />
                        Conta do Google
                      </button>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setSpendingHistoryModalOpen(false)}
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            {loadingSpending ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 10 }}>
                <Loader2 size={28} className="animate-spin" style={{ color: '#6366f1' }} />
                <span style={{ fontSize: '.78rem', color: 'rgba(255,255,255,0.5)' }}>Carregando histórico faturamento...</span>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.3fr', gap: 28, flex: 1, minHeight: 0, overflow: 'hidden' }}>

                {/* Coluna Esquerda: Gráfico + Pizza + KPIs (Fixo) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', paddingRight: 4 }}>

                  {/* Gráfico em Linha (SVG) com Previsão */}
                  {spendingHistory.length > 1 && (
                    <div style={{
                      background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', alignItems: 'center'
                    }}>
                      <div style={{ width: '100%', fontSize: '.68rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 6, fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
                        <span>Evolução Mensal do Faturamento (BRL)</span>
                        <span style={{ color: '#fbbf24' }}>Previsão Pontilhada (Laranja)</span>
                      </div>
                      <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ width: '100%', height: 130 }}>
                        <line x1="20" y1="20" x2={svgWidth - 20} y2="20" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                        <line x1="20" y1="65" x2={svgWidth - 20} y2="65" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                        <line x1="20" y1="110" x2={svgWidth - 20} y2="110" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                        <line x1="20" y1="140" x2={svgWidth - 20} y2="140" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />

                        {/* Linha Real */}
                        {realPoints.length > 0 && (
                          <polyline fill="none" stroke="#6366f1" strokeWidth="2.5" points={realPolyline} />
                        )}
                        {/* Linha de Previsão */}
                        {forecastPoints.length > 1 && (
                          <polyline fill="none" stroke="#fbbf24" strokeWidth="2.5" strokeDasharray="5" points={forecastPolyline} />
                        )}

                        {combinedHistory.map((h, i) => {
                          const pt = linePoints[i];
                          if (!pt) return null;
                          return (
                            <g key={i}>
                              <circle cx={pt.x} cy={pt.y} r="3.5" fill={pt.isForecast ? '#f59e0b' : '#a855f7'} />
                              <text x={pt.x} y={pt.y - 7} fill="#fff" fontSize="8" fontWeight="bold" textAnchor="middle">
                                {formatBRL(pt.total).split(',')[0]}
                              </text>
                              <text x={pt.x} y={svgHeight - 4} fill="rgba(255,255,255,0.4)" fontSize="7" textAnchor="middle">
                                {pt.mes.split(' ')[0]}
                              </text>
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                  )}

                  {/* Sub-colunas: KPIs (Esquerda) e Pizza/Donut (Direita) */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 16, marginTop: 4 }}>

                    {/* KPIs empilhados verticalmente */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, padding: 12 }}>
                        <span style={{ fontSize: '.65rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Total Faturado</span>
                        <div style={{ fontSize: '1.1rem', fontWeight: 800, marginTop: 2, color: '#fbbf24' }}>{formatBRL(totalSpendingAcumulado)}</div>
                      </div>

                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, padding: 12 }}>
                        <span style={{ fontSize: '.65rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Média Mensal</span>
                        <div style={{ fontSize: '1.1rem', fontWeight: 800, marginTop: 2 }}>{formatBRL(mediaSpendingMensal)}</div>
                      </div>

                      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10, padding: 12 }}>
                        <span style={{ fontSize: '.65rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>Faturamento Atual</span>
                        <div style={{ fontSize: '1.1rem', fontWeight: 800, marginTop: 2, color: '#34d399' }}>{formatBRL(data.custos.gastoAtual)}</div>
                      </div>
                    </div>

                    {/* Gráfico Donut/Pizza Interativo com Legenda */}
                    <div style={{
                      background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center'
                    }}>
                      <span style={{ fontSize: '.65rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', fontWeight: 700 }}>Divisão Média dos Recursos</span>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>

                        {/* Gráfico Donut SVG */}
                        <div style={{ position: 'relative', width: 85, height: 85, flexShrink: 0 }}>
                          <svg viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                            {pieData.map((slice, idx) => {
                              const strokeOffset = donutCircumference - (donutCircumference * slice.percent) / 100;
                              const currentRotation = (accumulatedPercent / 100) * donutCircumference;
                              accumulatedPercent += slice.percent;

                              return (
                                <circle
                                  key={slice.name}
                                  cx="60"
                                  cy="60"
                                  r="50"
                                  fill="none"
                                  stroke={slice.color}
                                  strokeWidth="12"
                                  strokeDasharray={`${donutCircumference} ${donutCircumference}`}
                                  strokeDashoffset={strokeOffset}
                                  style={{
                                    transform: `rotate(${(currentRotation / donutCircumference) * 360}deg)`,
                                    transformOrigin: '50% 50%',
                                    transition: 'all 0.3s',
                                    cursor: 'pointer'
                                  }}
                                  onMouseEnter={() => setHoveredPieSegment({ name: slice.name, percent: slice.percent, value: slice.value })}
                                  onMouseLeave={() => setHoveredPieSegment(null)}
                                />
                              );
                            })}
                          </svg>

                          <div style={{
                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                            textAlign: 'center', width: '80%', display: 'flex', flexDirection: 'column', justifyItems: 'center', alignItems: 'center'
                          }}>
                            <span style={{ fontSize: '.58rem', fontWeight: 700, color: '#fff', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', width: '100%' }}>
                              {hoveredPieSegment ? hoveredPieSegment.name.split(' ')[0] : 'GCP'}
                            </span>
                            <span style={{ fontSize: '.58rem', color: '#fbbf24', fontWeight: 800 }}>
                              {hoveredPieSegment ? `${hoveredPieSegment.percent}%` : '100%'}
                            </span>
                          </div>
                        </div>

                        {/* Legendas das Cores */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
                          {pieData.map(slice => (
                            <div
                              key={slice.name}
                              style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', opacity: hoveredPieSegment && hoveredPieSegment.name !== slice.name ? 0.4 : 1, transition: 'opacity .2s' }}
                              onMouseEnter={() => setHoveredPieSegment({ name: slice.name, percent: slice.percent, value: slice.value })}
                              onMouseLeave={() => setHoveredPieSegment(null)}
                            >
                              <span style={{ background: slice.color, width: 6, height: 6, borderRadius: '50%', flexShrink: 0 }} />
                              <span style={{ fontSize: '.6rem', color: 'rgba(255,255,255,0.7)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', flex: 1 }}>
                                {slice.name.split(' (')[0]}
                              </span>
                              <span style={{ fontSize: '.6rem', fontWeight: 700 }}>{slice.percent}%</span>
                            </div>
                          ))}
                        </div>

                      </div>

                      {hoveredPieSegment && (
                        <div style={{
                          background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)',
                          borderRadius: 6, padding: '4px 8px', fontSize: '.63rem', color: '#a7f3d0', width: '100%', textAlign: 'center'
                        }}>
                          Média: <strong>{formatBRL(hoveredPieSegment.value)}</strong> / mês
                        </div>
                      )}

                    </div>

                  </div>

                </div>

                {/* Coluna Direita: Tabela do Histórico (Fontes reduzidas, accordion com total acoplado no cabeçalho) */}
                <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}>
                  <div style={{ flex: 1, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 10 }}>
                    <table className="telemetry-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#1f2937', color: 'rgba(255,255,255,0.6)', zIndex: 12 }}>
                        <tr>
                          <th style={{ padding: '6px 8px' }}>Período</th>
                          <th style={{ padding: '6px 8px', textAlign: 'center' }}>Projeção</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Cloud SQL</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Cloud Run</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Storage</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>SMTP</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>IA</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>TTS</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Total (BRL)</th>
                        </tr>
                      </thead>
                      <tbody>

                        {/* ════ 1. ACCORDION HEADER PREVISÃO (Com Totais na mesma linha) ════ */}
                        {(() => {
                          const sumSql = calculatedForecast.reduce((acc, f) => acc + (f.detalhes.find(d => d.resource.includes('SQL'))?.cost || 0), 0);
                          const sumRun = calculatedForecast.reduce((acc, f) => acc + (f.detalhes.find(d => d.resource.includes('Run'))?.cost || 0), 0);
                          const sumStorage = calculatedForecast.reduce((acc, f) => acc + (f.detalhes.find(d => d.resource.includes('Storage'))?.cost || 0), 0);
                          const sumSmtp = calculatedForecast.reduce((acc, f) => acc + (f.detalhes.find(d => d.resource.includes('SMTP'))?.cost || 0), 0);
                          const sumAi = calculatedForecast.reduce((acc, f) => acc + (f.detalhes.find(d => d.resource.includes('Vertex'))?.cost || 0), 0);
                          const sumTts = calculatedForecast.reduce((acc, f) => acc + (f.detalhes.find(d => d.resource.includes('TTS'))?.cost || 0), 0);
                          const sumTotal = calculatedForecast.reduce((acc, f) => acc + f.total, 0);

                          const isExpanded = !!expandedYears['PREVISAO'];

                          return (
                            <React.Fragment>
                              <tr
                                style={{
                                  background: 'rgba(251,191,36,0.02)',
                                  fontStyle: 'italic',
                                  borderBottom: '1px solid rgba(251,191,36,0.15)',
                                  fontSize: '.56rem'
                                }}
                              >
                                <td style={{ padding: '8px 10px', color: 'rgba(255, 255, 255, 0.5)' }}>
                                  <div
                                    onClick={() => toggleYear('PREVISAO')}
                                    style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                                  >
                                    {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                    <span>Previsão</span>
                                  </div>
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                  <select
                                    value={forecastMonthsCount}
                                    onChange={e => setForecastMonthsCount(Number(e.target.value))}
                                    style={{
                                      background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255, 255, 255, 0.2)',
                                      borderRadius: 4, padding: '1px 4px', color: 'rgba(255, 255, 255, 0.5)', fontSize: '.54rem', outline: 'none',
                                      fontStyle: 'italic'
                                    }}
                                  >
                                    <option value="3" style={{ background: '#111827' }}>3m</option>
                                    <option value="6" style={{ background: '#111827' }}>6m</option>
                                    <option value="12" style={{ background: '#111827' }}>12m</option>
                                  </select>
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'rgba(255,255,255,0.5)' }}>{formatBRL(sumSql)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'rgba(255,255,255,0.5)' }}>{formatBRL(sumRun)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'rgba(255,255,255,0.5)' }}>{formatBRL(sumStorage)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'rgba(255,255,255,0.5)' }}>{formatBRL(sumSmtp)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'rgba(255,255,255,0.5)' }}>{formatBRL(sumAi)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'rgba(255,255,255,0.5)' }}>{formatBRL(sumTts)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.5)', fontWeight: 800, fontStyle: 'italic' }}>{formatBRL(sumTotal)}</td>
                              </tr>

                              {/* Linhas da Previsão expandidas (Sem ícone crystal) */}
                              {isExpanded && (() => {
                                const sortedForecast = [...calculatedForecast].reverse();
                                return sortedForecast.map((f, i) => {
                                  const prevMonthObj = i === sortedForecast.length - 1
                                    ? calculatedReal[calculatedReal.length - 1]
                                    : sortedForecast[i + 1];

                                  const sql = f.detalhes.find(d => d.resource.includes('SQL'))?.cost || 0;
                                  const run = f.detalhes.find(d => d.resource.includes('Run'))?.cost || 0;
                                  const storage = f.detalhes.find(d => d.resource.includes('Storage'))?.cost || 0;
                                  const smtp = f.detalhes.find(d => d.resource.includes('SMTP'))?.cost || 0;
                                  const ai = f.detalhes.find(d => d.resource.includes('Vertex'))?.cost || 0;
                                  const tts = f.detalhes.find(d => d.resource.includes('TTS'))?.cost || 0;

                                  const prevSql = prevMonthObj?.detalhes.find(d => d.resource.includes('SQL'))?.cost;
                                  const prevRun = prevMonthObj?.detalhes.find(d => d.resource.includes('Run'))?.cost;
                                  const prevStorage = prevMonthObj?.detalhes.find(d => d.resource.includes('Storage'))?.cost;
                                  const prevSmtp = prevMonthObj?.detalhes.find(d => d.resource.includes('SMTP'))?.cost;
                                  const prevAi = prevMonthObj?.detalhes.find(d => d.resource.includes('Vertex'))?.cost;
                                  const prevTts = prevMonthObj?.detalhes.find(d => d.resource.includes('TTS'))?.cost;
                                  const prevTotal = prevMonthObj?.total;

                                  return (
                                    <tr key={f.mes} className="hover-row" style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', background: 'rgba(251,191,36,0.02)', fontStyle: 'italic' }}>
                                      <td style={{ padding: '6px 8px', fontWeight: 700, color: 'rgba(255, 255, 255, 0.5)' }}>
                                        {f.mes.split(' ')[0]}
                                      </td>
                                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                        {renderGrowthWithArrow(f.growth || 0)}
                                      </td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'rgba(255,255,255,0.5)' }}>{renderValueWithArrow(sql, prevSql, 'SQL', 'PREVISAO', f.mes)}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'rgba(255,255,255,0.5)' }}>{renderValueWithArrow(run, prevRun, 'Run', 'PREVISAO', f.mes)}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'rgba(255,255,255,0.5)' }}>{renderValueWithArrow(storage, prevStorage, 'Storage', 'PREVISAO', f.mes)}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'rgba(255,255,255,0.5)' }}>{renderValueWithArrow(smtp, prevSmtp, 'SMTP', 'PREVISAO', f.mes)}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'rgba(255,255,255,0.5)' }}>{renderValueWithArrow(ai, prevAi, 'Vertex', 'PREVISAO', f.mes)}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'rgba(255,255,255,0.5)' }}>{renderValueWithArrow(tts, prevTts, 'TTS', 'PREVISAO', f.mes)}</td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>{renderValueWithArrow(f.total, prevTotal, 'Total', 'PREVISAO', f.mes)}</td>
                                    </tr>
                                  );
                                });
                              })()}
                            </React.Fragment>
                          );
                        })()}

                        {/* ════ 2. HISTÓRICO REAL AGRUPADO POR ANOS ════ */}
                        {Object.keys(groupedByYear).sort((a, b) => b.localeCompare(a)).map(year => {
                          const yearRows = groupedByYear[year];
                          const isExpanded = !!expandedYears[year];

                          const sumSql = yearRows.reduce((acc, h) => acc + (h.detalhes.find(d => d.resource.includes('SQL'))?.cost || 0), 0);
                          const sumRun = yearRows.reduce((acc, h) => acc + (h.detalhes.find(d => d.resource.includes('Run'))?.cost || 0), 0);
                          const sumStorage = yearRows.reduce((acc, h) => acc + (h.detalhes.find(d => d.resource.includes('Storage'))?.cost || 0), 0);
                          const sumSmtp = yearRows.reduce((acc, h) => acc + (h.detalhes.find(d => d.resource.includes('SMTP'))?.cost || 0), 0);
                          const sumAi = yearRows.reduce((acc, h) => acc + (h.detalhes.find(d => d.resource.includes('Vertex'))?.cost || 0), 0);
                          const sumTts = yearRows.reduce((acc, h) => acc + (h.detalhes.find(d => d.resource.includes('TTS'))?.cost || 0), 0);
                          const sumTotal = yearRows.reduce((acc, h) => acc + h.total, 0);

                          // Ordena meses decrescente para visualização tabular (Julho -> Janeiro)
                          const sortedRows = [...yearRows].sort((x, y) => {
                            const monthsOrder = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
                            const mX = monthsOrder.indexOf(x.mes.split(' ')[0]);
                            const mY = monthsOrder.indexOf(y.mes.split(' ')[0]);
                            return mY - mX;
                          });

                          return (
                            <React.Fragment key={year}>
                              {/* Accordion Header do Ano (Com Totais já na mesma linha do título) */}
                              <tr
                                style={{
                                  background: 'rgba(255,255,255,0.06)', fontWeight: 'bold',
                                  borderBottom: '1px solid rgba(255,255,255,0.1)'
                                }}
                              >
                                <td
                                  onClick={() => toggleYear(year)}
                                  style={{ padding: '8px 10px', color: '#fbbf24', cursor: 'pointer' }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                    <span>Ano {year}</span>
                                  </div>
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'center', color: '#fbbf24' }}>Real</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#fbbf24' }}>{formatBRL(sumSql)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#fbbf24' }}>{formatBRL(sumRun)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#fbbf24' }}>{formatBRL(sumStorage)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#fbbf24' }}>{formatBRL(sumSmtp)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#fbbf24' }}>{formatBRL(sumAi)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#fbbf24' }}>{formatBRL(sumTts)}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#ef4444', fontWeight: 800 }}>{formatBRL(sumTotal)}</td>
                              </tr>

                              {/* Linhas dos Meses Reais */}
                              {isExpanded && sortedRows.map(h => {
                                const sql = h.detalhes.find(d => d.resource.includes('SQL'))?.cost || 0;
                                const run = h.detalhes.find(d => d.resource.includes('Run'))?.cost || 0;
                                const storage = h.detalhes.find(d => d.resource.includes('Storage'))?.cost || 0;
                                const smtp = h.detalhes.find(d => d.resource.includes('SMTP'))?.cost || 0;
                                const ai = h.detalhes.find(d => d.resource.includes('Vertex'))?.cost || 0;
                                const tts = h.detalhes.find(d => d.resource.includes('TTS'))?.cost || 0;

                                const prevMonthObj = getPrevRealMonth(h.mes);
                                const prevSql = prevMonthObj?.detalhes.find(d => d.resource.includes('SQL'))?.cost;
                                const prevRun = prevMonthObj?.detalhes.find(d => d.resource.includes('Run'))?.cost;
                                const prevStorage = prevMonthObj?.detalhes.find(d => d.resource.includes('Storage'))?.cost;
                                const prevSmtp = prevMonthObj?.detalhes.find(d => d.resource.includes('SMTP'))?.cost;
                                const prevAi = prevMonthObj?.detalhes.find(d => d.resource.includes('Vertex'))?.cost;
                                const prevTts = prevMonthObj?.detalhes.find(d => d.resource.includes('TTS'))?.cost;
                                const prevTotal = prevMonthObj?.total;

                                return (
                                  <tr key={h.mes} className="hover-row" style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                                    <td style={{ padding: '6px 8px', fontWeight: 700, color: '#cbd5e1' }}>
                                      {h.mes.split(' ')[0]}
                                    </td>
                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                      {renderGrowthWithArrow(h.growth || 0)}
                                    </td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{renderValueWithArrow(sql, prevSql, 'SQL', year, h.mes)}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{renderValueWithArrow(run, prevRun, 'Run', year, h.mes)}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{renderValueWithArrow(storage, prevStorage, 'Storage', year, h.mes)}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{renderValueWithArrow(smtp, prevSmtp, 'SMTP', year, h.mes)}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{renderValueWithArrow(ai, prevAi, 'Vertex', year, h.mes)}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{renderValueWithArrow(tts, prevTts, 'TTS', year, h.mes)}</td>
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#fbbf24' }}>
                                      {renderValueWithArrow(h.total, prevTotal, 'Total', year, h.mes)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}

                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
